// Copyright 2026 Google LLC
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     https://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { CacheManager } from '../utils/cacheManager';
import { QualityParser } from '../utils/qualityParser';
import { normalizeHardware, normalizeModelName } from '../utils/dataParser';
import { parseJsonEntry, parseLogFile, parseLpgManifest, parseLpgConfig } from '../utils/dataParser';
import { parseReportV02, groupStagesIntoRuns, stageToEntry, isPristineScannedRun, forwardBundleMetadata, stripDerivedTimeSeries, rehydrateDerivedTimeSeries } from '../utils/benchmarkReportV02Parser';
import { scanLocalBenchmarks } from '../utils/gcsScanner';
import { useGCS } from './useGCS';
import { useGIQ } from './useGIQ';
import { useLLMD } from './useLLMD';
import { useAWS } from './useAWS';
import { useGitHubAuth } from './useGitHubAuth';
import { v4 as uuidv4 } from 'uuid';
import { getBenchmarkKey } from '../utils/dashboardHelpers';
import { getCanonicalBucketName, getBucketAlias, dedupeBucketConfigs, getBucketBaseName, getBucketPrefix } from '../utils/bucketUtils';

const getPrefixForBucket = (entry) => {
    // A "bucket/path" scoped entry restricts the scan to that subdirectory.
    const explicitPrefix = getBucketPrefix(entry);
    if (explicitPrefix) {
        return explicitPrefix;
    }
    const baseName = getBucketBaseName(entry);
    if (baseName === 'llm-d-benchmarks' || baseName === 'llm-d-benchmarks-staging') {
        return 'prism-results-store/';
    }
    return '';
};

export const useDashboardData = (initialState, dashboardState) => {
    const { selectedBenchmarks, setSelectedBenchmarks, xAxisMax, setXAxisMax } = dashboardState;
    const { accessToken, user } = useGitHubAuth();
    const pendingRequests = useRef(new Map());
    const [data, setData] = useState([]);
    const dataRef = useRef(data);
    useEffect(() => { dataRef.current = data; }, [data]);
    const [loading, setLoading] = useState(true);
    const [gcsLoading, setGcsLoading] = useState(false);
    const [gcsError, setGcsError] = useState(null);
    const [gcsSuccess, setGcsSuccess] = useState(null);
    const [apiError, setApiError] = useState(null);
    const [gcsProgress, setGcsProgress] = useState({});
    const [loadingTasks, setLoadingTasks] = useState({});

    const updateTaskProgress = useCallback((id, updates) => {
        setLoadingTasks(prev => {
            const existing = prev[id] || { id, name: id, loaded: 0, total: 0, status: 'pending', currentAction: '' };
            return {
                ...prev,
                [id]: { ...existing, ...updates }
            };
        });
    }, []);

    const handleProgress = useCallback(({ loaded, total, bucketName }) => {
        setGcsProgress(prev => ({
            ...prev,
            [bucketName]: { loaded, total }
        }));
        updateTaskProgress(`gcs:${bucketName}`, {
            loaded,
            total,
            status: loaded === total ? 'completed' : 'loading',
            currentAction: loaded === total ? 'Completed' : `Fetched ${loaded} of ${total} files`
        });
    }, [updateTaskProgress]);

    const gcsProgressStats = useMemo(() => {
        let loaded = 0;
        let total = 0;
        Object.values(gcsProgress).forEach(p => {
            loaded += p.loaded;
            total += p.total;
        });
        return { loaded, total };
    }, [gcsProgress]);
    const [lpgLoading, setLpgLoading] = useState(false);
    const [lpgError, setLpgError] = useState(null);
    const [lpgPasteText, setLpgPasteText] = useState("");
    const [brv02Runs, setBrv02Runs] = useState(() => {
        try {
            const saved = localStorage.getItem('prism_brv02_runs');
            // Time series are stripped before persisting, so rebuild them from
            // the stored rawReport.
            return saved ? rehydrateDerivedTimeSeries(JSON.parse(saved)) : [];
        } catch { return []; }
    });
    const [brv02Error, setBrv02Error] = useState(null);
    const [brv02Loading, setBrv02Loading] = useState(false);
    const [scannedFilenames, setScannedFilenames] = useState(null);
    const [brv02CustomLabels, setBrv02CustomLabels] = useState(() => {
        try {
            const saved = localStorage.getItem('prism_brv02_custom_labels');
            return saved ? JSON.parse(saved) : {};
        } catch { return {}; }
    });
    const [brv02BaselineRunId, setBrv02BaselineRunId] = useState(() => {
        try {
            return localStorage.getItem('prism_brv02_baseline_run_id') || null;
        } catch { return null; }
    });
    const [brv02SelectedStages, setBrv02SelectedStages] = useState(() => {
        try {
            const saved = localStorage.getItem('prism_brv02_selected_stages');
            return saved ? JSON.parse(saved) : {};
        } catch { return {}; }
    });

    useEffect(() => {
        try {
            localStorage.setItem('prism_brv02_runs', JSON.stringify(stripDerivedTimeSeries(brv02Runs)));
        } catch (e) {
            console.error("Failed to persist brv02 runs to LocalStorage:", e);
        }
    }, [brv02Runs]);

    useEffect(() => {
        try {
            localStorage.setItem('prism_brv02_custom_labels', JSON.stringify(brv02CustomLabels));
        } catch (e) {
            console.error("Failed to persist brv02 custom labels to LocalStorage:", e);
        }
    }, [brv02CustomLabels]);

    useEffect(() => {
        try {
            if (brv02BaselineRunId) {
                localStorage.setItem('prism_brv02_baseline_run_id', brv02BaselineRunId);
            } else {
                localStorage.removeItem('prism_brv02_baseline_run_id');
            }
        } catch (e) {
            console.error("Failed to persist brv02 baseline run ID to LocalStorage:", e);
        }
    }, [brv02BaselineRunId]);

    useEffect(() => {
        try {
            localStorage.setItem('prism_brv02_selected_stages', JSON.stringify(brv02SelectedStages));
        } catch (e) {
            console.error("Failed to persist brv02 selected stages to LocalStorage:", e);
        }
    }, [brv02SelectedStages]);

    useEffect(() => {
        // Automatically sync all benchmark stage runs into the main scatter plot data array
        const brv02Entries = brv02Runs.flatMap(run => run.stages.map(stageToEntry));
        const runSourceKeys = brv02Runs.map(r => `brv02:${r.runId}`);

        setData(prev => {
            const nonBrv02Data = prev.filter(d => !d.source?.startsWith('brv02:'));
            const startId = nonBrv02Data.length;
            const entriesWithIds = brv02Entries.map((e, i) => ({ ...e, id: startId + i }));
            return [...nonBrv02Data, ...entriesWithIds];
        });

        if (runSourceKeys.length > 0) {
            setAvailableSources(prev => {
                const next = new Set(prev);
                runSourceKeys.forEach(k => next.add(k));
                return next;
            });

            setSelectedSources(prev => {
                const next = new Set(prev);
                runSourceKeys.forEach(k => next.add(k));
                return next;
            });
        } else {
            setAvailableSources(prev => {
                const next = new Set(prev);
                Array.from(next).forEach(k => {
                    if (k.startsWith('brv02:')) next.delete(k);
                });
                return next;
            });
            setSelectedSources(prev => {
                const next = new Set(prev);
                Array.from(next).forEach(k => {
                    if (k.startsWith('brv02:')) next.delete(k);
                });
                return next;
            });
        }
    }, [brv02Runs]);
    const [driveLoading, setDriveLoading] = useState(false);
    const [driveStatus, setDriveStatus] = useState("");
    const [driveProgress, setDriveProgress] = useState(0);
    const [driveError, setDriveError] = useState(null);
    const [qualityMetrics, setQualityMetrics] = useState(null);
    const [availableSources, setAvailableSources] = useState(() => {
        return new Set(['local', 'llm-d-results:google_drive', 'llmd_drive']);
    });
    const [selectedSources, setSelectedSources] = useState(() => {
        if (initialState?.sources && initialState.sources.size > 0) {
            return initialState.sources;
        }
        try {
            const savedStr = localStorage.getItem('prism_selected_sources');
            if (savedStr) {
                const saved = JSON.parse(savedStr);
                if (Array.isArray(saved) && saved.length > 0) {
                    return new Set(saved);
                }
            }
        } catch (e) {
            console.warn("Failed to load selected sources from local storage", e);
        }
        return new Set(['local', 'llm-d-results:google_drive', 'llmd_drive']);
    });

    useEffect(() => {
        try {
            localStorage.setItem('prism_selected_sources', JSON.stringify(Array.from(selectedSources)));

            if (typeof window !== 'undefined') {
                const params = new URLSearchParams(window.location.search);
                if (params.has('src')) {
                    params.delete('src');
                    const newSearch = params.toString();
                    const newUrl = `${window.location.pathname}${newSearch ? '?' + newSearch : ''}${window.location.hash || ''}`;
                    if (window.location.search !== (newSearch ? `?${newSearch}` : '')) {
                        window.history.replaceState(null, '', newUrl);
                    }
                }
            }
        } catch (e) {
            console.warn("Failed to save selected sources to local storage", e);
        }
    }, [selectedSources]);
    const [bucketConfigs, setBucketConfigs] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem('prism_saved_sources') || '{}');
            const raw = saved.buckets || initialState?.buckets || [];
            return dedupeBucketConfigs(raw);
        } catch { return dedupeBucketConfigs(initialState?.buckets || []); }
    });
    const [awsBucketConfigs, setAwsBucketConfigs] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem('prism_saved_sources') || '{}');
            const raw = saved.awsBuckets || initialState?.awsBuckets || [];
            return dedupeBucketConfigs(raw);
        } catch { return dedupeBucketConfigs(initialState?.awsBuckets || []); }
    });
    const [apiConfigs, setApiConfigs] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem('prism_saved_sources') || '{}');
            const projects = saved.giqProjects || saved.apis || initialState?.giqProjects || [];
            if (projects.length > 0) {
                return projects.map(p => ({ projectId: p, token: localStorage.getItem(`giq_token_${p}`) || '' }));
            }
        } catch { }
        return [];
    });
    const [gcsProfiles, setGcsProfiles] = useState([]);
    const [enableLLMDResults, setEnableLLMDResults] = useState(true);
    const [toasts, setToasts] = useState([]);
    const [siteName, setSiteName] = useState("");
    const [contactUrl, setContactUrl] = useState("");
    const [gitCommit, setGitCommit] = useState("");
    const [gitDescribe, setGitDescribe] = useState("");
    const [newBucketName, setNewBucketName] = useState("");
    const [newBucketAlias, setNewBucketAlias] = useState("");
    const [connectionType, setConnectionType] = useState("gcs");
    const [newProjectId, setNewProjectId] = useState("");
    const [newAuthToken, setNewAuthToken] = useState("");
    const [showSampleData, setShowSampleData] = useState(true);
    const [expandedModels, setExpandedModels] = useState(new Set());
    const [debugInfo, setDebugInfo] = useState(null);
    const [qualityInspectOpen, setQualityInspectOpen] = useState(false);
    const [expandedIntegration, setExpandedIntegration] = useState(null);
    const isRestored = useRef(false);

    // True while restoreConnections is running — keeps the loading spinner active
    // until all saved GCS/GIQ sources have been fetched. Initialized to true if there
    // are any saved connections (determined synchronously from localStorage).
    const hasSavedConnections = (() => {
        try {
            const s = JSON.parse(localStorage.getItem('prism_saved_sources') || '{}');
            return (s.buckets?.length > 0) || (s.awsBuckets?.length > 0) ||
                (s.giqProjects?.length > 0) || (s.apis?.length > 0);
        } catch { return false; }
    })();
    const [isRestoringConnections, setIsRestoringConnections] = useState(hasSavedConnections);

    const API_KEY = window.env?.GOOGLE_API_KEY || import.meta.env.VITE_GOOGLE_API_KEY || import.meta.env.REACT_APP_GOOGLE_API_KEY || '';

    useEffect(() => {
        if (!isRestored.current) return;
        const cleanBuckets = dedupeBucketConfigs(bucketConfigs);
        localStorage.setItem('bucketConfigs', JSON.stringify(cleanBuckets));
    }, [bucketConfigs]);

    useEffect(() => {
        if (!isRestored.current) return;
        const cleanBuckets = dedupeBucketConfigs(bucketConfigs);
        const cleanAws = dedupeBucketConfigs(awsBucketConfigs);
        const toSave = {
            buckets: cleanBuckets,
            awsBuckets: cleanAws,
            giqProjects: apiConfigs.map(c => typeof c === 'string' ? c : c.projectId),
            qualityScoresEnabled: selectedSources.has('quality_scores')
        };
        localStorage.setItem('prism_saved_sources', JSON.stringify(toSave));
    }, [bucketConfigs, awsBucketConfigs, apiConfigs, selectedSources]);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const addToast = useCallback((message, type = 'info') => {
        const id = Date.now() + Math.random();
        setToasts(prev => {
            const filtered = prev.filter(t => !(t.message === message && t.type === type));
            return [...filtered, { id, message, type }];
        });
        setTimeout(() => removeToast(id), 8000); // 8 Seconds
    }, [removeToast]);

    // --- Extracted Block ---
    // Drive Sync Function
    // [useLLMD hook injected]
    const { syncDriveData } = useLLMD({ setData, setSelectedSources, setAvailableSources, setDriveLoading, setDriveStatus, setDriveProgress, setDriveError, enableLLMDResults, setSelectedBenchmarks, API_KEY });

    // Trigger sync when enabled, but wait for initial load
    useEffect(() => {
        if (loading) return;

        if (enableLLMDResults) {
            syncDriveData();
        } else {
            // Remove data if disabled
            setData(prev => prev.filter(d => d.source !== 'llmd_drive'));
        }
    }, [enableLLMDResults, loading]);

    // --- Extracted Block ---
    const fetchConfig = async () => {
        try {
            console.log("[useDashboardData] fetchConfig START");
            const response = await fetch('/api/config');
            console.log(`[useDashboardData] fetchConfig Response Status: ${response.status}`);
            if (response.ok) {
                const { buckets, projects, hostProject, siteName, gaTrackingId, contactUrl, gitCommit: fetchedCommit, gitDescribe: fetchedDescribe } = await response.json();
                console.log(`[useDashboardData] fetchConfig DATA: buckets=${buckets?.length}, projects=${projects?.length}, host=${hostProject}`);

                if (siteName) {
                    setSiteName(siteName);
                    document.title = `Prism ${siteName}`;
                }

                if (contactUrl) {
                    setContactUrl(contactUrl);
                }

                if (fetchedCommit) {
                    setGitCommit(fetchedCommit);
                }

                if (fetchedDescribe) {
                    setGitDescribe(fetchedDescribe);
                }

                // Initialize Google Analytics if ID is provided
                if (gaTrackingId && !window.gtag) {
                    const script = document.createElement('script');
                    script.async = true;
                    script.src = `https://www.googletagmanager.com/gtag/js?id=${gaTrackingId}`;
                    document.head.appendChild(script);

                    window.dataLayer = window.dataLayer || [];
                    function gtag() { window.dataLayer.push(arguments); }
                    gtag('js', new Date());
                    gtag('config', gaTrackingId);

                    window.gtag = gtag; // Prevent re-initialization
                }

                if (buckets && buckets.length > 0) {
                    setBucketConfigs(prev => dedupeBucketConfigs([...prev, ...buckets]));
                    // Auto-select these default buckets
                    setSelectedSources(prev => {
                        const next = new Set(prev);
                        buckets.forEach(b => next.add(`gcs:${getCanonicalBucketName(b)}`));
                        return next;
                    });
                }

                if (projects && projects.length > 0) {
                    setApiConfigs(prev => {
                        // Avoid duplicates based on projectId
                        const existingIds = new Set(prev.map(c => c.projectId));
                        const newConfigs = [...prev];
                        projects.forEach(p => {
                            if (!existingIds.has(p)) {
                                newConfigs.push({ projectId: p, token: '' });
                            }
                        });
                        return newConfigs;
                    });
                }

                // Auto-Connect Host Project
                if (hostProject) {
                    const sourceKey = `giq:${hostProject}`;

                    // Check if this project is already being restored from saved settings
                    const savedJson = localStorage.getItem('prism_saved_sources');
                    const saved = savedJson ? JSON.parse(savedJson) : null;
                    const alreadyRestored = saved?.giqProjects?.includes(hostProject) || saved?.apis?.includes(hostProject);

                    // Ensure it's selected
                    setAvailableSources(s => new Set([...s, sourceKey]));
                    setSelectedSources(s => new Set([...s, sourceKey]));

                    console.log(`[Auto-Discover] hostProject=${hostProject}, alreadyRestored=${alreadyRestored}`);

                    // Always ensure it's in apiConfigs safely using prev to prevent race conditions
                    setApiConfigs(prev => {
                        if (prev.some(c => (typeof c === 'string' ? c : c.projectId) === hostProject)) return prev;
                        const storedToken = localStorage.getItem(`giq_token_${hostProject}`) || '';
                        return [...prev, { projectId: hostProject, token: storedToken }];
                    });

                    // Pre-populate gcsProfiles with loading:true
                    setGcsProfiles(p => {
                        if (p.some(x => x.bucketName === hostProject && x.type === 'giq')) return p;
                        return [...p, { bucketName: hostProject, loading: true, type: 'giq' }];
                    });
                }
                return { buckets, projects, hostProject };
            }
            return null;
        } catch (e) {
            console.error("Failed to fetch config", e);
            return null;
        }
    };

    // --- Extracted Block ---
    // [useGCS hook injected]
    const { fetchBucketData } = useGCS({ pendingRequests, addToast, accessToken });

    // --- Extracted Block ---
    // [useAWS hook injected]
    const { fetchAWSBucketData } = useAWS({ pendingRequests, addToast });

    // --- Extracted Block ---
    // [useGIQ hook injected]
    const { fetchGiqData } = useGIQ({ pendingRequests, addToast, setLoading });

    // --- Extracted Block ---
    const fetchQualityData = async (forceRefresh = false) => {
        setLoading(true);
        try {
            // Rely on QualityParser's internal caching which handles versioning/bumping
            const data = await QualityParser.getAggregatedQualityProfile(forceRefresh);
            setQualityMetrics(data);
            setLoading(false);
            return data;
        } catch (error) {
            console.error("Quality Fetch Error:", error);
            addToast(`[Error] Failed to fetch Quality Scores: ${error.message}`, 'error');
            setLoading(false);
            return null;
        }
    };

    // --- Extracted Block ---
    useEffect(() => {
        const restoreConnections = async () => {
            const savedJson = localStorage.getItem('prism_saved_sources');
            isRestored.current = true; // Safe to allow saves now
            if (!savedJson) return;

            try {
                const saved = JSON.parse(savedJson);
                const restoredBuckets = dedupeBucketConfigs(saved.buckets || []);
                const restoredAwsBuckets = dedupeBucketConfigs(saved.awsBuckets || []);
                const restoredApis = Array.from(new Set((saved.giqProjects || saved.apis || []).map(p => typeof p === 'string' ? p.trim() : p).filter(Boolean)));

                // Re-save localStorage if deduplication logic made changes
                const rawBucketsStr = JSON.stringify(saved.buckets || []);
                const rawAwsStr = JSON.stringify(saved.awsBuckets || []);
                const rawApisStr = JSON.stringify(saved.giqProjects || saved.apis || []);

                if (
                    rawBucketsStr !== JSON.stringify(restoredBuckets) ||
                    rawAwsStr !== JSON.stringify(restoredAwsBuckets) ||
                    rawApisStr !== JSON.stringify(restoredApis)
                ) {
                    console.log('[Deduplication] Cleaned duplicate or unnormalized entries in localStorage. Re-saving...');
                    const cleanedSaved = {
                        ...saved,
                        buckets: restoredBuckets,
                        awsBuckets: restoredAwsBuckets,
                        giqProjects: restoredApis
                    };
                    localStorage.setItem('prism_saved_sources', JSON.stringify(cleanedSaved));
                    localStorage.setItem('bucketConfigs', JSON.stringify(restoredBuckets));
                }

                setGcsProgress({});
                if (restoredBuckets.length === 0 && restoredAwsBuckets.length === 0 && restoredApis.length === 0 && !saved.qualityScoresEnabled) return;

                const initialTasks = {};
                restoredBuckets.forEach(b => {
                    const bName = getCanonicalBucketName(b);
                    initialTasks[`gcs:${bName}`] = {
                        id: `gcs:${bName}`,
                        name: `gs://${bName}`,
                        type: 'gcs',
                        loaded: 0,
                        total: 0,
                        status: 'pending',
                        currentAction: 'Pending fetch'
                    };
                });
                restoredAwsBuckets.forEach(b => {
                    const bName = getCanonicalBucketName(b);
                    initialTasks[`aws:${bName}`] = {
                        id: `aws:${bName}`,
                        name: `aws://${bName}`,
                        type: 'aws',
                        loaded: 0,
                        total: 0,
                        status: 'pending',
                        currentAction: 'Pending fetch'
                    };
                });
                restoredApis.forEach(pid => {
                    initialTasks[`giq:${pid}`] = {
                        id: `giq:${pid}`,
                        name: `GIQ: ${pid}`,
                        type: 'giq',
                        loaded: 0,
                        total: 0,
                        status: 'pending',
                        currentAction: 'Pending fetch'
                    };
                });
                setLoadingTasks(initialTasks);
                
                // 1. Instant Feedback: Render Cards in Loading State
                setBucketConfigs(prev => dedupeBucketConfigs([...prev, ...restoredBuckets]));
                setAwsBucketConfigs(prev => dedupeBucketConfigs([...prev, ...restoredAwsBuckets]));
                setApiConfigs(prev => {
                    const existingIds = new Set(prev.map(c => typeof c === 'string' ? c : c.projectId));
                    const newConfigs = [...prev];
                    restoredApis.forEach(pid => {
                        if (!existingIds.has(pid)) {
                            newConfigs.push({ projectId: pid, token: localStorage.getItem(`giq_token_${pid}`) || '' });
                        }
                    });
                    return newConfigs;
                });

                setGcsProfiles(prev => {
                    const existingGcs = new Set(prev.filter(p => p.type === 'gcs').map(p => p.bucketName));
                    const existingAws = new Set(prev.filter(p => p.type === 'aws').map(p => p.bucketName));
                    const existingGiq = new Set(prev.filter(p => p.type === 'giq').map(p => p.bucketName));

                    const newProfiles = [
                        ...restoredBuckets.filter(b => !existingGcs.has(getCanonicalBucketName(b))).map(b => ({
                            bucketName: getCanonicalBucketName(b),
                            alias: getBucketAlias(b) || undefined,
                            loading: true,
                            type: 'gcs'
                        })),
                        ...restoredAwsBuckets.filter(b => !existingAws.has(getCanonicalBucketName(b))).map(b => ({
                            bucketName: getCanonicalBucketName(b),
                            alias: getBucketAlias(b) || undefined,
                            loading: true,
                            type: 'aws'
                        })),
                        ...restoredApis.filter(pid => !existingGiq.has(pid)).map(pid => ({ bucketName: pid, loading: true, type: 'giq' }))
                    ];
                    return [...prev, ...newProfiles];
                });

                const allResults = [];

                // Fetch Buckets
                for (const b of restoredBuckets) {
                    const bName = getCanonicalBucketName(b);
                    try {
                        const prefix = getPrefixForBucket(bName);
                        const res = await fetchBucketData(bName, false, prefix, handleProgress);
                        if (!res.profile.error) {
                            allResults.push({ type: 'gcs', id: bName, alias: getBucketAlias(b), ...res });
                        } else {
                            updateTaskProgress(`gcs:${bName}`, { status: 'failed', currentAction: res.profile.error });
                            // Update individual profile error
                            setGcsProfiles(prev => prev.map(p =>
                                p.bucketName === bName && p.type === 'gcs'
                                    ? { ...p, loading: false, error: res.profile.error }
                                    : p
                            ));
                        }
                    } catch (err) {
                        updateTaskProgress(`gcs:${bName}`, { status: 'failed', currentAction: `Error: ${err.message || 'Failed to connect'}` });
                        setGcsProfiles(prev => prev.map(p =>
                            p.bucketName === bName && p.type === 'gcs'
                                ? { ...p, loading: false, error: "Failed to connect" }
                                : p
                        ));
                    }
                }

                // Fetch AWS Buckets
                for (const b of restoredAwsBuckets) {
                    const bName = getCanonicalBucketName(b);
                    try {
                        const res = await fetchAWSBucketData(bName);
                        if (!res.profile.error) {
                            allResults.push({ type: 'aws', id: bName, alias: getBucketAlias(b), ...res });
                        } else {
                            updateTaskProgress(`aws:${bName}`, { status: 'failed', currentAction: res.profile.error });
                            setGcsProfiles(prev => prev.map(p =>
                                p.bucketName === bName && p.type === 'aws'
                                    ? { ...p, loading: false, error: res.profile.error }
                                    : p
                            ));
                        }
                    } catch (err) {
                        updateTaskProgress(`aws:${bName}`, { status: 'failed', currentAction: `Error: ${err.message || 'Failed to connect'}` });
                        setGcsProfiles(prev => prev.map(p =>
                            p.bucketName === bName && p.type === 'aws'
                                ? { ...p, loading: false, error: "Failed to connect" }
                                : p
                        ));
                    }
                }

                // Fetch APIs
                for (const pid of restoredApis) {
                    const token = localStorage.getItem(`giq_token_${pid}`) || '';
                    try {
                        const giqProgressCallback = (progressInfo) => {
                            updateTaskProgress(`giq:${pid}`, {
                                ...progressInfo,
                                status: progressInfo.status || 'loading'
                            });
                        };
                        let res = await fetchGiqData(pid, token, false, giqProgressCallback);

                        // Auto-Retry Logic: If User Token Expired (401/403), retry with ADC
                        if (res.profile.error && token && (res.profile.error.includes('401') || res.profile.error.includes('403'))) {
                            console.log(`[Persistence] Token expired for ${pid}. Retrying with ADC...`);
                            const retryRes = await fetchGiqData(pid, '', false, giqProgressCallback);
                            if (!retryRes.profile.error) {
                                res = retryRes; // Success! Use retry result
                            }
                        }

                        console.log(`[Persistence] GIQ Fetch for ${pid} returned ${res?.entries?.length || 0} entries.`);

                        if (!res.profile.error) {
                            allResults.push({ type: 'giq', id: pid, ...res });
                        } else {
                            updateTaskProgress(`giq:${pid}`, { status: 'failed', currentAction: res.profile.error });
                            // If backend returns error (e.g. 401/403 despite ADC), show it
                            setGcsProfiles(prev => prev.map(p =>
                                p.bucketName === pid && p.type === 'giq'
                                    ? { ...p, loading: false, error: res.profile.error }
                                    : p
                            ));
                        }
                    } catch (err) {
                        updateTaskProgress(`giq:${pid}`, { status: 'failed', currentAction: `Error: ${err.message || 'Connection Failed'}` });
                        setGcsProfiles(prev => prev.map(p =>
                            p.bucketName === pid && p.type === 'giq'
                                ? { ...p, loading: false, error: "Connection Failed" }
                                : p
                        ));
                    }
                }

                if (allResults.length > 0) {
                    // Batch update data
                    setData(prev => {
                        let next = [...prev];
                        allResults.forEach((r) => {
                            const sourceKey = `${r.type}:${r.id}`;
                            const normalized = r.entries.map(e => {
                                const hw = normalizeHardware(e.hardware);
                                return { ...e, hardware: hw, metadata: { ...e.metadata, hardware: hw }, source: sourceKey };
                            });
                            next = [...next, ...normalized];
                        });
                        return next.map((d, i) => ({ ...d, id: i }));
                    });

                    // Update Profiles with Success (preserving any custom aliases)
                    setGcsProfiles(prev => {
                        const updated = [...prev];
                        allResults.forEach(r => {
                            const idx = updated.findIndex(p => p.bucketName === r.id && p.type === r.type);
                            const effectiveAlias = r.alias || updated[idx]?.alias || r.profile?.alias;
                            if (idx !== -1) {
                                updated[idx] = {
                                    ...updated[idx],
                                    ...r.profile,
                                    alias: effectiveAlias,
                                    rawResponse: r.rawResponse,
                                    loading: false,
                                    type: r.type,
                                    bucketName: r.id
                                };
                            } else {
                                updated.push({
                                    ...r.profile,
                                    alias: effectiveAlias,
                                    rawResponse: r.rawResponse,
                                    loading: false,
                                    type: r.type,
                                    bucketName: r.id
                                });
                            }
                        });
                        return updated;
                    });

                    const newSources = allResults.map(r => `${r.type}:${r.id}`);
                    setAvailableSources(prev => new Set([...prev, ...newSources]));
                    setSelectedSources(prev => new Set([...prev, ...newSources]));

                    // Update models
                    const allKeys = new Set();
                    allResults.forEach(r => {
                        const sourceKey = `${r.type}:${r.id}`;
                        r.entries.forEach(e => {
                            allKeys.add(getBenchmarkKey({ ...e, source: sourceKey }));
                        });
                    });

                    setSelectedBenchmarks(prev => {
                        if (prev.size > 0) return prev;
                        try {
                            const savedSel = localStorage.getItem('prism_selected_benchmarks');
                            if (savedSel !== null) return prev;
                        } catch {
                            // ignore
                        }
                        // Default to qwen3-coder-next if present
                        const qwenKeys = Array.from(allKeys).filter(k => {
                            const parts = k.split('::');
                            if (parts.length > 2) {
                                const modelLower = parts[2].toLowerCase();
                                return modelLower.includes('qwen3-coder-next') || modelLower.includes('qwen3-code-next');
                            }
                            return false;
                        });
                        if (qwenKeys.length > 0) {
                            return new Set(qwenKeys);
                        }
                        return allKeys;
                    });
                }

                if (saved.qualityScoresEnabled) {
                    fetchQualityData(false);
                    setSelectedSources(prev => { const n = new Set(prev); n.add('quality_scores'); return n; });
                    setAvailableSources(prev => { const n = new Set(prev); n.add('quality_scores'); return n; });
                }
            } catch (e) {
                console.error("Failed to restore connections", e);
            } finally {
                setIsRestoringConnections(false);
            }
        };

        if (isRestoringConnections) {
            restoreConnections();
        } else {
            isRestored.current = true;
        }
    }, []);

    // --- Extracted Block ---
    useEffect(() => {
        if (loading) {
            const t = setTimeout(() => {
                console.warn("Loading took too long. Forcing completion.");
                setLoading(false);
                setGcsLoading(false);
            }, 60000);
            return () => clearTimeout(t);
        }
    }, [loading]);

    const updateSourceData = (sourceKey, newEntries, profile) => {
        const normalized = newEntries.map(e => ({ ...e, source: sourceKey }));
        const [type, bucketName] = sourceKey.split(':');
        const finalProfile = {
            bucketName,
            type,
            ...profile
        };

        setData(prev => {
            // Remove existing entries for this source
            const filtered = prev.filter(d => d.source !== sourceKey);
            // Add new entries, ensuring unique IDs
            const next = [...filtered, ...normalized].map((d, i) => ({ ...d, id: i }));
            return next;
        });

        setGcsProfiles(prev => {
            const existing = prev.filter(p => `${p.type}:${p.bucketName}` !== sourceKey);
            return [...existing, finalProfile];
        });

        setAvailableSources(prev => new Set([...prev, sourceKey]));
        setSelectedSources(prev => new Set([...prev, sourceKey]));

        const newKeys = normalized.map(d => getBenchmarkKey(d));
        setSelectedBenchmarks(prev => {
            const next = new Set(prev);
            if (prev.size === 0 && newKeys.length > 0) {
                // Auto-select a representative if none selected
                const llama = newKeys.find(k => k.toLowerCase().includes('llama'));
                next.add(llama || newKeys[0]);
            }
            return next;
        });
    };

    const handleAddGCSBucket = async (alias = null, bucketNameOverride = null) => {
        const nameToUse = bucketNameOverride || newBucketName;
        if (!nameToUse) return;
        const cleanName = getCanonicalBucketName(nameToUse);

        const exists = bucketConfigs.some(b => getCanonicalBucketName(b) === cleanName);

        if (exists) {
            setGcsError('Bucket already configured.');
            return;
        }

        setGcsProgress({});
        setGcsLoading(true);
        const prefix = getPrefixForBucket(cleanName);
        const result = await fetchBucketData(cleanName, false, prefix, handleProgress);
        setGcsLoading(false);

        if (result.profile.error) {
            setGcsError(`GCS Error: ${result.profile.error}`);
        } else {
            const cleanAlias = alias ? alias.trim() : null;
            const newEntry = cleanAlias ? { bucket: cleanName, alias: cleanAlias } : cleanName;
            setBucketConfigs(prev => dedupeBucketConfigs([...prev, newEntry]));

            const finalProfile = { ...result.profile, alias: cleanAlias || cleanName, type: 'gcs' };
            updateSourceData(`gcs:${cleanName}`, result.entries, finalProfile);

            setNewBucketName('');
            setGcsSuccess(`Added bucket: ${cleanAlias || cleanName}`);
            setTimeout(() => setGcsSuccess(null), 3000);
        }
    };

    const handleAddAWSBucket = async (alias = null, bucketNameOverride = null) => {
        const nameToUse = bucketNameOverride || newBucketName;
        if (!nameToUse) return;
        const cleanName = getCanonicalBucketName(nameToUse);

        const exists = awsBucketConfigs.some(b => getCanonicalBucketName(b) === cleanName);

        if (exists) {
            setGcsError('AWS Bucket already configured.');
            return;
        }

        setGcsLoading(true);
        const result = await fetchAWSBucketData(cleanName);
        setGcsLoading(false);

        if (result.profile.error) {
            setGcsError(`AWS Error: ${result.profile.error}`);
        } else {
            const cleanAlias = alias ? alias.trim() : null;
            const newEntry = cleanAlias ? { bucket: cleanName, alias: cleanAlias } : cleanName;

            setAwsBucketConfigs(prev => dedupeBucketConfigs([...prev, newEntry]));

            setSelectedSources(prev => new Set([...prev, `aws:${cleanName}`]));
            setAvailableSources(prev => new Set([...prev, `aws:${cleanName}`]));

            const finalProfile = { ...result.profile, alias: cleanAlias || cleanName, type: 'aws' };

            updateSourceData(`aws:${cleanName}`, result.entries, finalProfile);

            const newModels = [...new Set(result.entries.map(d => d.model).filter(m => m !== 'Unknown'))];
            setSelectedBenchmarks(prev => {
                const next = new Set(prev);
                if (prev.size === 0 && newModels.length > 0) {
                    const candidate = newModels.find(m => m.toLowerCase().includes('llama')) || newModels[0];
                    next.add(candidate);
                }
                return next;
            });

            setNewBucketName('');
            setGcsSuccess(`Added AWS bucket: ${cleanAlias || cleanName}`);
            setTimeout(() => setGcsSuccess(null), 3000);
        }
    };

    const handleAddGIQProject = async (projectIdOverride = null, tokenOverride = null) => {
        const idToUse = projectIdOverride || newProjectId;
        const tokenToUse = tokenOverride || newAuthToken;
        if (!idToUse) return;

        const exists = apiConfigs.some(c => (typeof c === 'string' ? c : c.projectId) === idToUse);
        if (exists) {
            setGcsError('Project ID already configured.');
            return;
        }

        setGcsLoading(true);
        const result = await fetchGiqData(idToUse, tokenToUse);
        setGcsLoading(false);

        if (result.profile.error) {
            setGcsError(`GIQ Error: ${result.profile.error}`);
        } else {
            setApiConfigs(prev => [...prev, { projectId: idToUse, token: tokenToUse }]);
            localStorage.setItem(`giq_token_${idToUse}`, tokenToUse); // Persist token

            const finalProfile = { ...result.profile, bucketName: idToUse, type: 'giq' };
            updateSourceData(`giq:${idToUse}`, result.entries, finalProfile);

            setNewProjectId('');
            setNewAuthToken('');
            setGcsSuccess(`Added GIQ Project: ${idToUse}`);
            setTimeout(() => setGcsSuccess(null), 3000);
        }
    };

    const removeGCSBucket = (bucketName) => {
        const cleanTarget = getCanonicalBucketName(bucketName);
        const newConfigs = bucketConfigs.filter(b => getCanonicalBucketName(b) !== cleanTarget);
        setBucketConfigs(newConfigs);

        const sourceKey = `gcs:${cleanTarget}`;
        const newSources = new Set(selectedSources);
        newSources.delete(sourceKey);
        setSelectedSources(newSources);
        setAvailableSources(prev => {
            const n = new Set(prev);
            n.delete(sourceKey);
            return n;
        });

        setData(prev => prev.filter(d => d.source !== sourceKey).map((d, i) => ({ ...d, id: i })));
        setGcsProfiles(prev => prev.filter(p => `gcs:${p.bucketName}` !== sourceKey));
    };

    const removeAWSBucket = (bucketName) => {
        const cleanTarget = getCanonicalBucketName(bucketName);
        const newConfigs = awsBucketConfigs.filter(b => getCanonicalBucketName(b) !== cleanTarget);
        setAwsBucketConfigs(newConfigs);

        const sourceKey = `aws:${cleanTarget}`;
        const newSources = new Set(selectedSources);
        newSources.delete(sourceKey);
        setSelectedSources(newSources);
        setAvailableSources(prev => {
            const n = new Set(prev);
            n.delete(sourceKey);
            return n;
        });

        setData(prev => prev.filter(d => d.source !== sourceKey).map((d, i) => ({ ...d, id: i })));
        setGcsProfiles(prev => prev.filter(p => `aws:${p.bucketName}` !== sourceKey));
    };

    const removeGIQProject = (projectId) => {
        const newConfigs = apiConfigs.filter(c => c.projectId !== projectId);
        setApiConfigs(newConfigs);
        localStorage.removeItem(`giq_token_${projectId}`); // Remove persisted token

        const sourceKey = `giq:${projectId}`;
        const newSources = new Set(selectedSources);
        newSources.delete(sourceKey);
        setSelectedSources(newSources);
        setAvailableSources(prev => {
            const n = new Set(prev);
            n.delete(sourceKey);
            return n;
        });

        setData(prev => prev.filter(d => d.source !== sourceKey).map((d, i) => ({ ...d, id: i })));
        setGcsProfiles(prev => prev.filter(p => `giq:${p.bucketName}` !== sourceKey));
    };

    // --- Extracted Block ---
    const fetchLocalData = async () => {
        try {
            const results = [];
            let hasDataJson = false;

            // 1. Fetch data.json (Standard Local Sample)
            try {
                const res = await fetch('/data.json');
                if (res.ok) {
                    const contentType = res.headers.get("content-type");
                    if (contentType && contentType.indexOf("application/json") !== -1) {
                        const json = await res.json();
                        if (Array.isArray(json)) {
                            results.push(...json);
                            hasDataJson = true;
                        }
                    }
                }
            } catch (e) {
                console.warn("Could not load /data.json", e);
            }

            // Note: llm-d-benchmarks.json is now loaded via fetchArchivedData as part of the LLM-D Results Store integration.

            if (results.length === 0) {
                console.warn("No local data found (checked /data.json and /data/llm-d-benchmarks.json)");
                return [];
            }

            return results.map((d, i) => {
                const newD = { ...d, _raw: d._raw || d, source: d.source || 'local' };

                // Allow extraction of ISL/OSL from model string if needed
                // Format: "... (..., 8000/1000, ...)"
                if (newD.model && newD.model.includes('/') && (!newD.isl || !newD.osl)) {
                    const match = newD.model.match(/, (\d+)\/(\d+),/);
                    if (match) {
                        newD.isl = parseInt(match[1]);
                        newD.osl = parseInt(match[2]);
                    }
                }

                // Hoist Nested Metrics (Crucial for llm-d-benchmarks.json compatibility)
                if (newD.metrics) {
                    if (newD.throughput === undefined) newD.throughput = newD.metrics.throughput || newD.metrics.total_token_throughput || 0;
                    if (newD.latency === undefined || !newD.latency.mean) newD.latency = newD.metrics.latency || newD.latency;
                    if (newD.ttft === undefined || !newD.ttft.mean) newD.ttft = newD.metrics.ttft || newD.ttft;
                    // Fix for missing TPOT on chart
                    if (newD.time_per_output_token === undefined) newD.time_per_output_token = newD.metrics.tpot || newD.metrics.mean_tpot_ms || 0;
                }

                // Robust Metadata Backfill
                newD.metadata = newD.metadata || {};
                newD.metadata.model_name = newD.metadata.model_name || newD.model_name || newD.model?.split('(')[0]?.trim() || 'Unknown';
                newD.metadata.hardware = newD.metadata.hardware || newD.hardware || 'Unknown';
                newD.metadata.precision = newD.metadata.precision || newD.precision || 'Unknown';
                newD.metadata.backend = newD.metadata.backend || newD.backend || 'Unknown';
                newD.metadata.configuration = newD.metadata.configuration || newD.configuration || 'Unknown';
                newD.metadata.tensor_parallelism = newD.metadata.tensor_parallelism || newD.tensor_parallelism || newD.tp || 8; // Default to 8 if missing/legacy
                newD.metadata.prefill_node_count = newD.prefill_node_count;
                newD.metadata.decode_node_count = newD.decode_node_count;

                if (newD.isl) newD.metadata.input_seq_len = newD.isl;
                if (newD.osl) newD.metadata.output_seq_len = newD.osl;

                // Normalize model names
                if (newD.model_name) newD.model_name = normalizeModelName(newD.model_name);
                if (newD.metadata?.model_name) newD.metadata.model_name = normalizeModelName(newD.metadata.model_name);

                // Normalize Hardware/Accelerator
                newD.hardware = normalizeHardware(newD.hardware);
                if (newD.metadata) newD.metadata.hardware = newD.hardware;
                if (!newD.accelerator_count && newD.metadata?.accelerator_count) {
                    newD.accelerator_count = newD.metadata.accelerator_count;
                }

                // Add Source Info if missing (Critical for Inspector)
                if (!newD.source_info) {
                    newD.source_info = {
                        type: newD.source === 'local' ? 'local' : 'file',
                        origin: newD.source || 'file',
                        file_identifier: newD.filename || 'data.json',
                        raw_url: newD.raw_url || null
                    };
                }

                // Add Diagnostics if missing
                if (!newD._diagnostics) {
                    newD._diagnostics = {
                        msg: [],
                        raw_snapshot: d // Use original d as snapshot
                    };
                }

                // ms to us conversion for legacy reasons if needed, but keeping standard
                if (newD.latency?.mean && newD.latency.mean < 100) {
                    newD.latency.mean *= 1000;
                    if (newD.latency.p50) newD.latency.p50 *= 1000;
                    if (newD.latency.p99) newD.latency.p99 *= 1000;
                    if (newD.latency.min) newD.latency.min *= 1000;
                    if (newD.latency.max) newD.latency.max *= 1000;
                }
                if (newD.ttft?.mean && newD.ttft.mean < 100) {
                    newD.ttft.mean *= 1000;
                    if (newD.ttft.p50) newD.ttft.p50 *= 1000;
                    if (newD.ttft.p99) newD.ttft.p99 *= 1000;
                    if (newD.ttft.min) newD.ttft.min *= 1000;
                    if (newD.ttft.max) newD.ttft.max *= 1000;
                }
                return newD;
            });
        } catch (e) {
            console.error("Failed to load local data", e);
            throw e;
        }
    };

    // --- Extracted Block ---
    async function fetchArchivedData() {
        updateTaskProgress('archive:google_drive', { status: 'loading', loaded: 0, total: 2, currentAction: 'Fetching archived drive data...' });
        try {
            const files = [
                '/data/archive/llmd_results/archived_drive_data.json',
                '/data/archive/llmd_results/llm-d-benchmarks.json'
            ];

            let loadedCount = 0;
            const results = await Promise.all(files.map(async (file) => {
                try {
                    const res = await fetch(`${file}?t=${Date.now()}`);
                    if (!res.ok) return [];
                    const json = await res.json();
                    if (!Array.isArray(json)) return [];

                    const filename = file.split('/').pop();
                    return json.map(j => ({ ...j, _source_file: filename }));
                } catch (e) {
                    console.warn(`Failed to load ${file}`, e);
                    return [];
                } finally {
                    loadedCount++;
                    updateTaskProgress('archive:google_drive', { loaded: loadedCount, currentAction: `Loaded ${loadedCount} of ${files.length} archive files` });
                }
            }));
            updateTaskProgress('archive:google_drive', { status: 'completed', currentAction: 'Completed' });

            const allBenchmarks = results.flat();
            console.log(`Loaded ${allBenchmarks.length} unified archived benchmarks from ${files.length} files.`);

            return allBenchmarks.map((d, i) => {
                const newD = { ...d, _raw: d, source: 'llm-d-results:google_drive' };
                // Extract Sweep ID from run_id (e.g., prefix before -run_ or -vllm-)
                const runId = d.run_id || 'unknown';
                let sweepId = runId;
                if (runId.includes('-run_')) sweepId = runId.split('-run_')[0];
                else if (runId.includes('-vllm-')) sweepId = runId.split('-vllm-')[0];
                else if (runId.includes('-setup_')) sweepId = runId.split('-setup_')[0];

                // Ensure valid source info
                newD.source_info = {
                    type: 'drive_archive',
                    origin: sweepId !== 'unknown' ? sweepId : 'llm-d Results Store', // Fallback for fixed static files
                    run_id: runId,   // Keep original run_id
                    file_identifier: d._source_file || d.filename || 'archived_data'
                };

                // Robust Configuration Parsing from Folder Structure (User Requirement)
                // Matches patterns like: pd-disaggregation.setup_standalone_1_2_NA_NA_NA_NA
                const pathString = (d.id || '') + (d.source_info?.origin || '') + (d.source_info?.file_identifier || '') + (d.filename || '');

                // Standalone Parser: setup_standalone_<Nodes>_<TP>_...
                const stdMatch = pathString.match(/setup_standalone_(\d+)_(\d+)_/);
                if (stdMatch) {
                    const nodes = parseInt(stdMatch[1], 10);
                    const tp = parseInt(stdMatch[2], 10);
                    newD.accelerator_count = nodes * tp;
                    newD.architecture = 'aggregated';
                    newD.metadata = newD.metadata || {};
                    newD.metadata.accelerator_count = newD.accelerator_count; // Sync for grouping logic
                    newD.metadata.tensor_parallelism = tp;
                    newD.metadata.node_count = nodes;
                    newD.metadata.configuration = `${nodes} Node${nodes > 1 ? 's' : ''} (TP${tp})`;
                    newD.pd_ratio = 'Aggregated';
                    // Ensure Model Name in Table reflects this config if multiple exist (though ideally clean)
                    // We rely on the "Nodes" and "Chips" columns to distinguish them.
                }

                // Disaggregated Parser: setup_modelservice_NA_NA_<P_Node>_<P_TP>_<D_Node>_<D_TP>
                const disaggMatch = pathString.match(/setup_modelservice_NA_NA_(\d+)_(\d+)_(\d+)_(\d+)/);
                if (disaggMatch) {
                    const pNode = parseInt(disaggMatch[1], 10);
                    const pTp = parseInt(disaggMatch[2], 10);
                    const dNode = parseInt(disaggMatch[3], 10);
                    const dTp = parseInt(disaggMatch[4], 10);

                    newD.accelerator_count = (pNode * pTp) + (dNode * dTp);
                    newD.architecture = 'disaggregated';
                    newD.metadata = newD.metadata || {};
                    newD.metadata.accelerator_count = newD.accelerator_count; // Sync for grouping logic
                    newD.metadata.prefill_node_count = pNode;
                    newD.metadata.prefill_tp = pTp;
                    newD.metadata.decode_node_count = dNode;
                    newD.metadata.decode_tp = dTp;

                    // Enforce Standard Configuration Format
                    const totalNodes = pNode + dNode;
                    newD.metadata.configuration = `${totalNodes}: ${pNode}P-TP${pTp} ${dNode}D-TP${dTp}`;
                    newD.pd_ratio = `${pNode}:${dNode}`;
                }

                // Setup Inf Parser: inference-perf_...-setup_inf_sche_...-run_...
                // Matches patterns like: setup_inf_sche_none_yaml
                const infMatch = pathString.match(/setup_inf_sche_([a-z0-9_]+)/i);
                if (infMatch && !newD.metadata.configuration) {
                    newD.metadata = newD.metadata || {};
                    newD.metadata.variant = infMatch[1].replace(/_yaml$/i, '');
                    newD.metadata.configuration = `Serving: ${newD.metadata.variant}`;
                    newD.architecture = 'aggregated';
                    newD.pd_ratio = 'Aggregated';
                }

                // Robust Metadata Backfill (Copied from fetchLocalData to ensure compatibility)
                newD.metadata = newD.metadata || {};
                newD.metadata.model_name = normalizeModelName(newD.metadata.model_name || newD.model_name || 'Unknown');
                newD.metadata.hardware = normalizeHardware(newD.metadata.hardware || newD.hardware || 'Unknown');
                newD.metadata.configuration = newD.metadata.configuration || newD.configuration || 'Unknown';
                newD.metadata.tensor_parallelism = newD.metadata.tensor_parallelism || newD.tensor_parallelism || newD.tp || 8;

                // Precision extraction from path/id
                let precision = 'Unknown';
                const lowerPath = pathString.toLowerCase();
                if (lowerPath.includes('fp4')) precision = 'FP4';
                else if (lowerPath.includes('fp8')) precision = 'FP8';
                else if (lowerPath.includes('int8')) precision = 'INT8';
                else if (lowerPath.includes('fp16')) precision = 'FP16';
                else if (lowerPath.includes('bf16') || lowerPath.includes('bfloat16')) precision = 'BF16';

                newD.precision = precision;
                newD.metadata.precision = precision;

                // FLATTEN METRICS for Dashboard Compatibility
                if (newD.metrics) {
                    newD.throughput = Number(newD.metrics.throughput || newD.metrics.total_token_throughput || 0);
                    newD.tokens_per_second = newD.throughput; // Chart compatibility

                    newD.latency = newD.metrics.latency || newD.latency || { mean: 0, p50: 0, p99: 0 };
                    // Ensure mean is a number
                    if (newD.latency && typeof newD.latency.mean !== 'number') newD.latency.mean = Number(newD.latency.mean || 0);

                    newD.ttft = newD.metrics.ttft || newD.ttft || { mean: 0, p50: 0 };
                    if (newD.ttft && typeof newD.ttft.mean !== 'number') newD.ttft.mean = Number(newD.ttft.mean || 0);

                    const qpsVal = newD.workload?.target_qps ?? newD.metrics?.request_rate;
                    newD.qps = qpsVal != null ? Number(qpsVal) : null;
                    // Critical for chart filtering/plotting (chartMode === 'tpot')
                    newD.time_per_output_token = Number(newD.metrics.time_per_output_token || newD.metrics.tpot || newD.metrics.mean_tpot_ms || 0);
                    newD.tpot = newD.time_per_output_token;
                    newD.ntpot = newD.time_per_output_token;

                    // Ensure nested metrics match for chart getVal(metrics.ntpot)
                    newD.metrics.ntpot = newD.ntpot;
                    newD.metrics.throughput = newD.throughput;
                    newD.metrics.tokens_per_second = newD.throughput;
                }

                // FLATTEN WORKLOAD
                if (newD.workload) {
                    newD.isl = newD.workload.input_tokens ?? newD.isl ?? null;
                    newD.osl = newD.workload.output_tokens ?? newD.osl ?? null;
                    newD.prompt_len = newD.workload.input_tokens ?? newD.isl ?? null;
                    newD.output_len = newD.workload.output_tokens ?? newD.osl ?? null;
                }

                // FLATTEN DISAGGREGATED CONFIG (Critical for Table/Chart Display)
                newD.metadata.prefill_node_count = newD.metadata.prefill_node_count || newD.prefill_node_count || 0;
                newD.metadata.decode_node_count = newD.metadata.decode_node_count || newD.decode_node_count || 0;
                newD.metadata.prefill_tp = newD.metadata.prefill_tp || newD.prefill_tp || 0;
                newD.metadata.decode_tp = newD.metadata.decode_tp || newD.decode_tp || 0;

                // FLATTEN METADATA
                newD.model = newD.metadata.model_name;

                // Consistently append variant/configuration to model name for separation in UI
                let suffix = '';
                if (newD.metadata?.variant) {
                    suffix = ` [${newD.metadata.variant}]`;
                } else if (newD.metadata?.configuration && newD.metadata.configuration !== 'Unknown') {
                    // Use hardware config (e.g. from standalone/modelservice parser)
                    suffix = ` [${newD.metadata.configuration}]`;
                }
                if (suffix) {
                    // All three derive from metadata.model_name, the only one
                    // backfilled above: the top-level model_name is optional
                    // here, and appending to it directly yielded the literal
                    // string "undefined [kv]" whenever it was absent.
                    const suffixed = newD.metadata.model_name + suffix;
                    newD.model = suffixed;
                    newD.model_name = suffixed;
                    newD.metadata.model_name = suffixed;
                }

                return newD;
            });
        } catch (e) {
            console.error("Failed to load archived data", e);
            updateTaskProgress('archive:google_drive', { status: 'failed', currentAction: `Error: ${e.message}` });
            return [];
        }
    }

    const loadAllData = async (fetchedConfig = null, forceRefresh = false) => {
        console.log("[useDashboardData] loadAllData START", { initialState });
        setGcsProgress({});
        setLoading(true);
        setGcsLoading(true);
        setGcsError(null);

        const apisToFetch = [...apiConfigs];
        if (fetchedConfig && fetchedConfig.projects) {
            fetchedConfig.projects.forEach(p => {
                if (!apisToFetch.some(c => (typeof c === 'string' ? c : c.projectId) === p)) {
                    apisToFetch.push({ projectId: p, token: '' });
                }
            });
        }
        if (fetchedConfig && fetchedConfig.hostProject && !apisToFetch.some(c => (typeof c === 'string' ? c : c.projectId) === fetchedConfig.hostProject)) {
            apisToFetch.push({ projectId: fetchedConfig.hostProject, token: '' });
        }

        const bucketsToFetch = [...bucketConfigs];
        if (fetchedConfig && fetchedConfig.buckets) {
            fetchedConfig.buckets.forEach(b => {
                const bName = typeof b === 'string' ? b : b.bucket;
                if (!bucketsToFetch.some(c => (typeof c === 'string' ? c : c.bucket) === bName)) {
                    bucketsToFetch.push(b);
                }
            });
        }

        const initialTasks = {};
        if (enableLLMDResults) {
            initialTasks['archive:google_drive'] = {
                id: 'archive:google_drive',
                name: 'llm-d Google Drive',
                type: 'archive',
                loaded: 0,
                total: 2,
                status: 'pending',
                currentAction: 'Pending fetch'
            };
        }
        bucketsToFetch.forEach(b => {
            const bName = typeof b === 'string' ? b : b.bucket;
            initialTasks[`gcs:${bName}`] = {
                id: `gcs:${bName}`,
                name: `gs://${bName}`,
                type: 'gcs',
                loaded: 0,
                total: 0,
                status: 'pending',
                currentAction: 'Pending fetch'
            };
        });
        apisToFetch.forEach(config => {
            const projectId = typeof config === 'string' ? config : config.projectId;
            initialTasks[`giq:${projectId}`] = {
                id: `giq:${projectId}`,
                name: `GIQ: ${projectId}`,
                type: 'giq',
                loaded: 0,
                total: 0,
                status: 'pending',
                currentAction: 'Pending fetch'
            };
        });
        setLoadingTasks(initialTasks);

        const failedSources = [];

        try {
            // 1. Fetch Local Data
            let allData = [];
            try {
                const localEntries = await fetchLocalData();
                allData = [...localEntries];

                // If we explicitly fetched local data but it's empty, 
                // ensure the toggle reflects that it's not active by default.
                if (localEntries.length === 0) {
                    setShowSampleData(false);
                }
            } catch (e) {
                console.error("Failed to load local data", e);
                failedSources.push(`Sample Data (${e.message})`);
            }

            // 1b. Fetch local benchmark reports. These join brv02Runs, so they
            // render through the same derivation as uploads.
            try {
                const scan = await scanLocalBenchmarks();
                // Only a successful scan may reconcile: a failed one reports no
                // filenames, and acting on that would drop every local run.
                if (scan.ok) {
                    setScannedFilenames(scan.filenames);
                    setBrv02Runs(prev => [
                        // Drop pristine scanned runs, so a deleted file disappears.
                        // Both marks are required: grouping by load metadata can fuse
                        // an upload into a scanned run and spread the origin to it,
                        // but never the local: runId, which only the scanner mints.
                        ...prev.filter(r => !isPristineScannedRun(r)),
                        ...scan.runs.filter(s => !prev.some(r => r.runId === s.runId && !isPristineScannedRun(r))),
                    ]);
                }
            } catch (e) {
                console.warn("Failed to load local/PVC benchmarks", e);
            }

            // 1c. Fetch Archived Drive Data
            try {
                if (enableLLMDResults) {
                    const archivedEntries = await fetchArchivedData();
                    if (archivedEntries.length > 0) {
                        allData = [...allData, ...archivedEntries];
                        setAvailableSources(prev => new Set([...prev, 'llm-d-results:google_drive']));
                        // Force select archived_drive if locally available
                        setSelectedSources(prev => new Set([...prev, 'llm-d-results:google_drive']));
                    }
                }
            } catch (e) {
                console.warn("Failed to load archived data", e);
            }



            // 2. Fetch All Configured Buckets
            const bucketResults = await Promise.all(bucketsToFetch.map(b => {
                const bName = typeof b === 'string' ? b : b.bucket;
                const prefix = getPrefixForBucket(bName);
                return fetchBucketData(bName, forceRefresh, prefix, handleProgress)
                    .then(res => ({ ...res, config: b }))
                    .catch(err => {
                        console.error(`Failed to fetch GCS bucket ${bName}:`, err);
                        return {
                            bucketName: bName,
                            profile: { error: err.message || "Failed to connect", files: [], loadedAt: new Date().toISOString() },
                            entries: [],
                            config: b
                        };
                    });
            }));

            const newProfiles = [];
            bucketResults.forEach(res => {
                if (res) {
                    if (res.profile.error) {
                        failedSources.push(res.bucketName);
                    }

                    const normalizedEntries = (res.entries || []).map(e => {
                        const hw = normalizeHardware(e.hardware);
                        return {
                            ...e,
                            hardware: hw,
                            metadata: { ...e.metadata, hardware: hw },
                            source: `gcs:${res.bucketName}`
                        };
                    });

                    if (normalizedEntries.length > 0) {
                        allData = [...allData, ...normalizedEntries];
                    }

                    const alias = typeof res.config === 'object' ? res.config.alias : null;

                    newProfiles.push({
                        bucketName: res.bucketName,
                        alias: alias || res.bucketName, // Default to bucket name if no alias
                        files: res.profile.files,
                        entryCount: (res.entries || []).length,
                        visible: true,
                        error: res.profile.error || null,
                        loadedAt: res.profile.loadedAt,
                        type: 'gcs'
                    });
                }
            });

            // 3. Fetch API Sources
            const apiProfiles = [];

            for (const config of apisToFetch) {
                // Backward compatibility check
                const projectId = typeof config === 'string' ? config : config.projectId;
                const token = typeof config === 'string' ? '' : config.token;

                // Allow empty token for shared configuration (Backend Proxy handles auth)
                // if (!token) ... check removed.

                try {
                    const giqProgressCallback = (progressInfo) => {
                        updateTaskProgress(`giq:${projectId}`, {
                            ...progressInfo,
                            status: progressInfo.status || 'loading'
                        });
                    };
                    const apiData = await fetchGiqData(projectId, token, forceRefresh, giqProgressCallback);

                    // Check for profile error from fetchGiqData
                    if (apiData.profile.error) {
                        failedSources.push(`GIQ:${projectId}`);
                    }

                    apiProfiles.push({
                        bucketName: projectId,
                        files: [],
                        entryCount: apiData.entries.length,
                        visible: true,
                        error: apiData.entries.length === 0 ? (apiData.profile.error || "No data found") : null,
                        rawResponse: apiData.rawResponse,
                        profileCount: apiData.profile ? apiData.profile.profileCount : (apiData.rawResponse?.profile?.length || 0),
                        loadedAt: apiData.profile.loadedAt || new Date().toISOString(),
                        type: 'giq'
                    });

                    // We need to push entries, not the result object
                    if (apiData.entries) {
                        const normalizedEntries = apiData.entries.map(e => {
                            const hw = normalizeHardware(e.hardware);
                            return { ...e, hardware: hw, metadata: { ...e.metadata, hardware: hw }, source: `giq:${projectId}` };
                        });
                        allData.push(...normalizedEntries);
                    }
                } catch (e) {
                    console.warn(`Failed to fetch for project ${projectId}`, e);
                    failedSources.push(`GIQ:${projectId}`);
                    apiProfiles.push({
                        bucketName: projectId,
                        files: [],
                        entryCount: 0,
                        visible: true,
                        error: "Failed to connect",
                        type: 'giq',
                        rawResponse: { error: e.message }
                    });
                }
            }

            // Auto-load LPG sources from URL
            console.log("[useDashboardData] initialState.sources available for auto-load:", initialState.sources);
            if (initialState.sources) {
                for (const src of initialState.sources) {
                    if (src.startsWith('lpg:')) {
                        const bucketName = src.substring(4);
                        console.log(`[useDashboardData] Auto-loading LPG source: ${bucketName}`);
                        try {
                            const scanResult = await handleLpgGcsScan(`gs://${bucketName}`);
                            console.log(`[useDashboardData] Scan result for ${bucketName}:`, scanResult);
                            await handleLpgGcsLoad(`gs://${bucketName}`, scanResult.folderNames, scanResult.folders, scanResult.usingProxy);
                            console.log(`[useDashboardData] Successfully auto-loaded LPG source: ${bucketName}`);
                        } catch (e) {
                            console.warn(`Failed to auto-load LPG source ${bucketName}`, e);
                            failedSources.push(src);
                        }
                    }
                }
            }

            // Assign IDs
            const dataWithIds = allData.map((d, i) => ({ ...d, id: i }));

            console.log(`[useDashboardData] loadAllData FINISHED. Found ${dataWithIds.length} total benchmarks.`);

            setData(prev => {
                // Keep entries from prev that were NOT fetched by loadAllData to preserve concurrent fetches
                const loadedSources = new Set(dataWithIds.map(d => d.source));

                // For GIQ specifically, we need to be careful as they can fetch the same source.
                // If this is a fresh loadAllData, we generally want its data to take precedence for the sources it *did* fetch.
                const retainedPrev = prev.filter(p => !loadedSources.has(p.source));

                // Merge and recalculate IDs
                const merged = [...retainedPrev, ...dataWithIds];
                
                return merged.map((d, i) => ({ ...d, id: i }));
            });

            setGcsProfiles(prev => {
                const updated = [...prev];
                [...newProfiles, ...apiProfiles].forEach(newProf => {
                    const idx = updated.findIndex(p => p.bucketName === newProf.bucketName && p.type === newProf.type);
                    if (idx !== -1) {
                        updated[idx] = { ...updated[idx], ...newProf };
                    } else {
                        updated.push(newProf);
                    }
                });
                return updated;
            });

            // Extract unique sources from MERGED data
            setData(prev => {
                const currentSources = new Set(prev.map(d => d.source || 'local'));
                const validSources = new Set([...currentSources, ...dataWithIds.map(d => d.source || 'local')]);

                if (selectedSources.has('quality_scores')) {
                    validSources.add('quality_scores');
                }

                if (enableLLMDResults) {
                    validSources.add('llmd_drive');
                    validSources.add('llm-d-results:google_drive');
                }

                setAvailableSources(prev => new Set([...prev, ...validSources]));
                return prev;
            });

            if (failedSources.length > 0) {
                setGcsError(`Connection issue with: ${failedSources.join(', ')}`);
            }
        } catch (e) {
            console.error("loadAllData Error:", e);
            setGcsError(`Load failed: ${e.message}`);
        } finally {
            setLoading(false);
            setGcsLoading(false);
        }
    };

    const handleLpgGcsScan = async (bucketUrl) => {
        try {
            console.log(`[useDashboardData] GCS Scanning ${bucketUrl}...`);
            const cleanUrl = bucketUrl.replace(/^gs:\/\//, '').replace(/\/$/, '');
            const response = await fetch(`/api/gcs/scan?bucket=${cleanUrl}`);
            if (response.ok) {
                const results = await response.json();
                return results;
            } else {
                const errorText = await response.text();
                throw new Error(errorText || `HTTP ${response.status}`);
            }
        } catch (e) {
            console.error("GCS Scan Failed:", e);
            throw e;
        }
    };

    const handleLpgGcsLoad = async (bucketUrl, folderNames, folders, usingProxy) => {
        setLpgLoading(true);
        setLpgError(null);
        let folderCount = 0;
        let allNewEntries = [];

        try {
            const cleanBucketName = bucketUrl.replace(/^gs:\/\//, '').replace(/\/$/, '');
            const cleanUri = bucketUrl.replace(/\/$/, '');

            // Process folders in parallel
            await Promise.all(folderNames.map(async (folder) => {
                try {
                    const files = folders[folder];
                    // Relaxed search: find manifest and metrics ANYWHERE within this logical leaf node folder
                    // i.e., "config/manifest.yaml" or "stage_4_lifecycle_metrics.json"
                    const manifestFile = files.find(f => f.name.endsWith('manifest.yaml'));
                    const configFile = files.find(f => f.name.endsWith('config.yaml'));
                    const metricFiles = files.filter(f => f.name.endsWith('lifecycle_metrics.json') && !f.name.endsWith('summary_lifecycle_metrics.json'));

                    if (!metricFiles.length) return;

                    let syntheticMetadata = "";
                    let model = 'Unknown';
                    let hw = 'Unknown';
                    let count = 1;
                    let tp = 1;
                    let backend = 'vllm';
                    let precision = 'bfloat16'; // Usually bfloat16 for these tests

                    if (manifestFile) {
                        let manifestUrl = manifestFile.mediaLink;
                        if (usingProxy && manifestUrl.startsWith('https://storage.googleapis.com/')) {
                            manifestUrl = `/api/gcs/${manifestUrl.replace('https://storage.googleapis.com/', '')}`;
                        }
                        const manifestRes = await fetch(manifestUrl);
                        if (manifestRes.ok) {
                            const yamlTxt = await manifestRes.text();
                            const meta = parseLpgManifest(yamlTxt);
                            model = meta.model;
                            hw = meta.hw;
                            count = meta.count;
                            tp = meta.tp;
                            backend = meta.backend;
                        }
                    }

                    // Fallback to config.yaml for model name if manifest didn't provide it
                    if (model === 'Unknown' && configFile) {
                        let configUrl = configFile.mediaLink;
                        if (usingProxy && configUrl.startsWith('https://storage.googleapis.com/')) {
                            configUrl = `/api/gcs/${configUrl.replace('https://storage.googleapis.com/', '')}`;
                        }
                        const configRes = await fetch(configUrl);
                        if (configRes.ok) {
                            const configTxt = await configRes.text();
                            const meta = parseLpgConfig(configTxt);
                            if (meta.model && meta.model !== 'Unknown') {
                                model = meta.model;
                            }
                        }
                    }

                    if (manifestFile || configFile) {
                        syntheticMetadata = JSON.stringify({
                            config: { model, tensor_parallel: tp, backend, precision },
                            infrastructure: { accelerator_type: hw, accelerator_count: count }
                        }) + "\n";
                    }

                    for (const metricFile of metricFiles) {
                        let metricUrl = metricFile.mediaLink;
                        if (usingProxy && metricUrl.startsWith('https://storage.googleapis.com/')) {
                            metricUrl = `/api/gcs/${metricUrl.replace('https://storage.googleapis.com/', '')}`;
                        }
                        const metricRes = await fetch(metricUrl);
                        if (metricRes.ok) {
                            const metricTxt = await metricRes.text();
                            const combinedText = syntheticMetadata + metricTxt;
                            const entries = parseLogFile(combinedText, `${folder}/${metricFile.name}`);

                            if (entries.length > 0) {
                                entries.forEach(e => {
                                    e.source = `lpg:${cleanBucketName}`;
                                    e.source_info = {
                                        type: 'lpg',
                                        origin: `lpg:${cleanUri}`,
                                        file_identifier: `${folder}/${metricFile.name}`,
                                        raw_url: metricFile.mediaLink
                                    };
                                });
                                allNewEntries.push(...entries);
                            }
                        }
                    }
                    if (allNewEntries.length > 0) folderCount++;
                } catch (e) {
                    console.error(`Failed parsing folder ${folder}`, e);
                }
            }));

            if (allNewEntries.length > 0) {
                const sourceKey = `lpg:${cleanBucketName}`;
                // Keep data array clean, filter out ONLY the specific folders (origins) we are loading right now
                // so we don't accidentally wipe out other folders loaded from the exact same bucket.
                const loadingOrigins = new Set(allNewEntries.map(e => e.source_info?.origin));
                const filteredData = data.filter(d => !loadingOrigins.has(d.source_info?.origin));

                const startId = filteredData.length;
                const dataWithIds = allNewEntries.map((d, i) => ({
                    ...d, id: startId + i
                }));

                setData([...filteredData, ...dataWithIds]);
                setSelectedSources(prev => new Set([...prev, sourceKey]));
                setAvailableSources(prev => new Set([...prev, sourceKey]));
                setGcsSuccess(`Loaded ${allNewEntries.length} metrics from ${folderCount} ${folderCount === 1 ? 'folder' : 'folders'} in ${cleanBucketName}.`);
            } else {
                setLpgError("No valid benchmark metrics could be parsed from the bucket folders.");
            }
        } catch (e) {
            console.error("LPG GCS Load Error:", e);
            setLpgError(`Failed to load from GCS: ${e.message}`);
        } finally {
            setLpgLoading(false);
        }
    };

    const handleLpgFileUpload = async (event) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        setLpgLoading(true);
        setLpgError(null);
        let allNewEntries = [];
        const newSourceKeys = [];

        try {
            // Process files sequentially to avoid freezing UI
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const text = await file.text();
                const entries = parseLogFile(text, file.name);

                if (entries.length > 0) {
                    // Use filename as unique source key for this upload
                    const sourceKey = `infperf:${file.name}`;
                    newSourceKeys.push(sourceKey);

                    // Tag each entry with this specific source
                    entries.forEach(e => {
                        e.source = sourceKey;
                        e.source_info = {
                            ...e.source_info,
                            type: 'lpg',
                            file_identifier: file.name
                        };
                    });
                    allNewEntries.push(...entries);
                }
            }

            if (allNewEntries.length > 0) {
                // Assign unique IDs
                const startId = data.length;
                const dataWithIds = allNewEntries.map((d, i) => ({
                    ...d,
                    id: startId + i
                }));

                setData(prev => [...prev, ...dataWithIds]);

                // Add all new source keys to selection
                setSelectedSources(prev => {
                    const newSet = new Set(prev);
                    newSourceKeys.forEach(k => newSet.add(k));
                    return newSet;
                });
                setAvailableSources(prev => {
                    const newSet = newSet(prev);
                    newSourceKeys.forEach(k => newSet.add(k));
                    return newSet;
                });

                setGcsSuccess(`Successfully loaded ${allNewEntries.length} LPG metrics from ${newSourceKeys.length} file(s).`);
            } else {
                setLpgError("No valid LPG metrics found in selected files.");
            }
        } catch (err) {
            console.error("LPG Parse Error:", err);
            setLpgError("Failed to parse log files.");
        } finally {
            setLpgLoading(false);
            // Reset input
            event.target.value = '';
        }
    };

    // -------------------------------------------------------------------------
    // Benchmark Report v0.2 handlers
    // -------------------------------------------------------------------------

    const handleBrv02Upload = async (eventOrFiles) => {
        let files;
        let isEvent = false;
        if (eventOrFiles && eventOrFiles.target && eventOrFiles.target.files) {
            files = Array.from(eventOrFiles.target.files);
            isEvent = true;
        } else if (Array.isArray(eventOrFiles)) {
            files = eventOrFiles;
        } else if (eventOrFiles) {
            files = Array.from(eventOrFiles);
        }

        if (!files || files.length === 0) return;
        
        setBrv02Loading(true);
        setBrv02Error(null);

        try {
            const v02Pattern = /^benchmark_report_v0\.2.*\.ya?ml$/i;
            const matchingFiles = [];

            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                if (v02Pattern.test(file.name)) {
                    matchingFiles.push(file);
                }
            }

            if (matchingFiles.length === 0) {
                setBrv02Error("No valid benchmark_report_v0.2 files found. Make sure the files start with version: '0.2' and match the 'benchmark_report_v0.2' filename prefix.");
                if (isEvent && eventOrFiles.target) {
                    eventOrFiles.target.value = '';
                }
                return;
            }

            const trulyNewStages = [];
            for (let i = 0; i < matchingFiles.length; i++) {
                const file = matchingFiles[i];
                const text = await file.text();
                const identifier = file.webkitRelativePath || file.relativePath || file.name;
                const record = await parseReportV02(text, identifier);
                if (record) {
                    const isSameStage = (s) => {
                        if (s.runId && record.runId && s.runId !== record.runId) return false;
                        if (s.runEid && record.runEid && s.runEid !== record.runEid) return false;
                        if (s.runUid && record.runUid && s.runUid !== record.runUid) return false;
                        return s.filename === record.filename;
                    };
                    const isDupInBatch = trulyNewStages.some(isSameStage);
                    const isDupInExisting = brv02Runs.some(run => 
                        run.stages.some(isSameStage)
                    );
                    if (!isDupInBatch && !isDupInExisting) {
                        trulyNewStages.push(record);
                    }
                }
            }

            if (trulyNewStages.length === 0) {
                setBrv02Error('All selected files have already been submitted.');
                if (isEvent && eventOrFiles.target) {
                    eventOrFiles.target.value = '';
                }
                return;
            }

            // Update comparison panel state
            setBrv02Runs(prev => {
                const allStages = [...prev.flatMap(run => run.stages), ...trulyNewStages];
                return groupStagesIntoRuns(allStages);
            });

            if (isEvent && eventOrFiles.target) {
                eventOrFiles.target.value = '';
            }
        } catch (e) {
            console.error("Failed to submit local report files:", e);
            setBrv02Error("Failed to submit report files. Make sure they are valid YAML files.");
        } finally {
            setBrv02Loading(false);
        }
    };

    const restoreSampleData = async () => {
        setGcsLoading(true);
        try {
            const localEntries = await fetchLocalData();

            if (!localEntries || localEntries.length === 0) {
                setGcsLoading(false);
                return false;
            }

            // Add to data
            setData(prev => {
                const next = [...prev, ...localEntries].map((d, i) => ({ ...d, id: i }));
                return next;
            });

            // Update Sources
            setAvailableSources(prev => new Set([...prev, 'local']));
            setSelectedSources(prev => new Set([...prev, 'local'])); // Auto-select
            setShowSampleData(true);

            // Re-populate models for local data
            const localModels = new Set(localEntries.map(d => d.model).filter(m => m !== 'Unknown'));
            setSelectedBenchmarks(prev => {
                const next = new Set(prev);
                localModels.forEach(m => next.add(m));
                return next;
            });

            setGcsLoading(false);
            return true;
        } catch (e) {
            setGcsError(`Failed to restore sample data: ${e.message}`);
            setGcsLoading(false);
            return false;
        }
    };

    const removeSampleData = () => {
        // Remove 'local' source entries from data
        setData(prev => prev.filter(d => d.source !== 'local'));

        // Remove 'local' from selected and available sources
        setSelectedSources(prev => {
            const next = new Set(prev);
            next.delete('local');
            return next;
        });
        setAvailableSources(prev => {
            const next = new Set(prev);
            next.delete('local');
            return next;
        });

        setShowSampleData(false);
    };

    const removeLLMDData = () => {
        // Remove 'llmd_drive' (live syncs) and 'llm-d-results:google_drive' (archive) from data
        setData(prev => prev.filter(d => d.source !== 'llmd_drive' && d.source !== 'llm-d-results:google_drive'));

        // Remove from selected and available sources
        setSelectedSources(prev => {
            const next = new Set(prev);
            next.delete('llmd_drive');
            next.delete('llm-d-results:google_drive');
            return next;
        });
        setAvailableSources(prev => {
            const next = new Set(prev);
            next.delete('llmd_drive');
            next.delete('llm-d-results:google_drive');
            return next;
        });

        // Also clean up any lingering local data if needed, but the main two cover the standard flows.
        setEnableLLMDResults(false);
    };

    const removeBrv02Run = useCallback((runId) => {
        setBrv02Runs(prev => prev.filter(r => r.runId !== runId));
    }, []);

    const promoteStagedRunId = (oldRunId, newRunId) => {
        // 1. Remove from local staged runs list
        setBrv02Runs(prev => prev.filter(r => r.runId !== oldRunId));

        // 2. Transfer custom label configurations
        setBrv02CustomLabels(prev => {
            if (!prev[oldRunId]) return prev;
            const next = { ...prev };
            next[newRunId] = next[oldRunId];
            delete next[oldRunId];
            return next;
        });

        // 3. Transfer stage selection configuration
        setBrv02SelectedStages(prev => {
            if (!prev[oldRunId]) return prev;
            const next = { ...prev };
            next[newRunId] = next[oldRunId];
            delete next[oldRunId];
            return next;
        });

        // 4. Transfer baseline selection
        setBrv02BaselineRunId(prev => {
            if (prev === oldRunId) return newRunId;
            return prev;
        });
    };

    const clearAllBrv02Runs = useCallback(() => {
        setBrv02Runs([]);
    }, []);

    const handleValidatedUpload = async (validBundles) => {
        if (!validBundles || validBundles.length === 0) return;
        
        setBrv02Loading(true);
        setBrv02Error(null);

        try {
            // Get all runIds of the bundles we are staging/updating
            const stagedRunIds = new Set(validBundles.map(b => b.payload.runId || b.dirKey).filter(Boolean));
            validBundles.forEach(b => {
                if (b.originalSourceBundles) {
                    b.originalSourceBundles.forEach(orig => {
                        const origId = orig.payload?.runId || orig.dirKey || orig.id;
                        if (origId) stagedRunIds.add(origId);
                    });
                }
            });

            // Flatten and filter out old stages of the edited runs
            const otherStages = brv02Runs.flatMap(run => run.stages).filter(stage => !stagedRunIds.has(stage.runId));
            const trulyNewStages = [];
            
            for (const bundle of validBundles) {
                const config = bundle.metadataFiles.config ? bundle.metadataFiles.config.parsed : null;
                const summary = bundle.metadataFiles.summary ? bundle.metadataFiles.summary.parsed : null;
                const bundleRunId = bundle.payload.runId;
                const bundleRunLabel = bundle.payload.runLabel;

                // Resolved ID for the staged run
                const resolvedRunId = bundleRunId || null;

                if (bundle.payload?.entries && bundle.payload.entries.length > 0) {
                    for (let idx = 0; idx < bundle.payload.entries.length; idx++) {
                        const entry = bundle.payload.entries[idx];
                        const record = await parseReportV02(entry.raw_report || entry.content, entry.filename);
                        if (record) {
                            record.runId = resolvedRunId || record.runId;
                            record.runLabel = bundleRunLabel || record.runLabel;
                            record.run_id = entry.run_id || uuidv4();
                            record.prism_stage_index = entry.prism_stage_index !== undefined ? entry.prism_stage_index : idx;
                            if (record.stageIndex === null || record.stageIndex === undefined) {
                                record.stageIndex = record.prism_stage_index;
                            }
                            if (record.workload) {
                                record.workload.stage = record.prism_stage_index;
                            }
                            // Enrich stage record with bundle metadata
                            forwardBundleMetadata(record, bundle.payload);
                            record.config = config || bundle.payload?.config || null;
                            record.summary = summary || bundle.payload?.summary || null;
                            record.bundle = bundle;
                            record.targetDashboards = bundle.targetDashboards;
                            
                            const isDupInBatch = trulyNewStages.some(s => {
                                if (s.runId !== record.runId) return false;
                                if (s.run_id && record.run_id) return s.run_id === record.run_id;
                                if (s.prism_stage_index !== undefined && record.prism_stage_index !== undefined) {
                                    return s.prism_stage_index === record.prism_stage_index && s.filename === record.filename;
                                }
                                return s.filename === record.filename;
                            });
                            const isDupInExisting = otherStages.some(existingStage => {
                                if (existingStage.runId !== record.runId) return false;
                                if (existingStage.run_id && record.run_id) return existingStage.run_id === record.run_id;
                                if (existingStage.prism_stage_index !== undefined && record.prism_stage_index !== undefined) {
                                    return existingStage.prism_stage_index === record.prism_stage_index && existingStage.filename === record.filename;
                                }
                                return existingStage.filename === record.filename;
                            });
                            
                            if (!isDupInBatch && !isDupInExisting) {
                                trulyNewStages.push(record);
                            }
                        }
                    }
                } else if (bundle.stageFiles && bundle.stageFiles.length > 0) {
                    for (let idx = 0; idx < bundle.stageFiles.length; idx++) {
                        const sf = bundle.stageFiles[idx];
                        const identifier = sf.filename || sf.name || sf.file?.webkitRelativePath || sf.file?.name;
                        const record = await parseReportV02(sf.validation?.parsedData || sf.content, identifier);
                        if (record) {
                            record.runId = resolvedRunId || record.runId;
                            record.runLabel = bundleRunLabel || record.runLabel;
                            const matchingEntry = bundle.payload?.entries?.find(e => e.filename === record.filename);
                            record.run_id = matchingEntry?.run_id || uuidv4();
                            record.prism_stage_index = matchingEntry?.prism_stage_index !== undefined ? matchingEntry.prism_stage_index : idx;
                            if (record.stageIndex === null || record.stageIndex === undefined) {
                                record.stageIndex = record.prism_stage_index;
                            }
                            if (record.workload) {
                                record.workload.stage = record.prism_stage_index;
                            }
                            // Enrich stage record with bundle metadata
                            forwardBundleMetadata(record, bundle.payload);
                            record.config = config;
                            record.summary = summary;
                            record.bundle = bundle;
                            record.targetDashboards = bundle.targetDashboards;
                            
                            const isDupInBatch = trulyNewStages.some(s => {
                                if (s.runId !== record.runId) return false;
                                if (s.run_id && record.run_id) return s.run_id === record.run_id;
                                if (s.prism_stage_index !== undefined && record.prism_stage_index !== undefined) {
                                    return s.prism_stage_index === record.prism_stage_index && s.filename === record.filename;
                                }
                                return s.filename === record.filename;
                            });
                            const isDupInExisting = otherStages.some(existingStage => {
                                if (existingStage.runId !== record.runId) return false;
                                if (existingStage.run_id && record.run_id) return existingStage.run_id === record.run_id;
                                if (existingStage.prism_stage_index !== undefined && record.prism_stage_index !== undefined) {
                                    return existingStage.prism_stage_index === record.prism_stage_index && existingStage.filename === record.filename;
                                }
                                return existingStage.filename === record.filename;
                            });
                            
                            if (!isDupInBatch && !isDupInExisting) {
                                trulyNewStages.push(record);
                            }
                        }
                    }
                }
            }

            setBrv02Runs(() => {
                const allStages = [...otherStages, ...trulyNewStages];
                return groupStagesIntoRuns(allStages);
            });

        } catch (e) {
            console.error("Failed to submit validated files:", e);
            setBrv02Error("Failed to submit validated report files.");
        } finally {
            setBrv02Loading(false);
        }
    };

    const fetchBucketDelta = async (bucket, currentProfile) => {
        const cleanBucketName = bucket.replace(/^gs:\/\//, '');
        const prefix = currentProfile?.prefix || '';
        let usingProxy = false;

        try {
            const queryParams = new URLSearchParams();
            if (prefix) {
                queryParams.set('prefix', prefix);
            }
            const queryString = queryParams.toString();
            const suffix = queryString ? `?${queryString}` : '';
            let response = await fetch(`https://storage.googleapis.com/storage/v1/b/${cleanBucketName}/o${suffix}`);

            // Fallback to Proxy
            if (response.status === 401 || response.status === 403) {
                response = await fetch(`/api/gcs/storage/v1/b/${cleanBucketName}/o${suffix}`);
                if (response.ok) usingProxy = true;
            }

            if (!response.ok) throw new Error(`Failed to access bucket (${response.status}).`);

            const json = await response.json();
            if (!json.items) return { newEntries: [], newFiles: [] };

            const filesToProcess = json.items.filter(item => !item.name.endsWith('/'));

            // Find new files
            const existingFiles = new Set((currentProfile?.files || []).map(f => f.name));
            const newFilesToFetch = filesToProcess.filter(f => !existingFiles.has(f.name));

            if (newFilesToFetch.length === 0) return { newEntries: [], newFiles: [] };

            const newEntries = [];
            const newFileMetadata = [];

            await Promise.all(newFilesToFetch.map(async (file) => {
                try {
                    let fileUrl = file.mediaLink;
                    if (usingProxy && fileUrl.startsWith('https://storage.googleapis.com/')) {
                        const path = fileUrl.replace('https://storage.googleapis.com/', '');
                        fileUrl = `/api/gcs/${path}`;
                    }

                    const fileRes = await fetch(fileUrl);
                    if (!fileRes.ok) throw new Error(`Fetch failed: ${fileRes.status}`);

                    const content = await fileRes.text();
                    let entries = [];
                    try {
                        const jsonContent = JSON.parse(content);
                        if (jsonContent.metrics || jsonContent.load_summary) {
                            const entry = parseJsonEntry({ ...jsonContent, source: `gcs:${cleanBucketName}` }, file.name);
                            entries = [entry];
                        }
                    } catch {
                        // Ignore JSON parse failures
                    }

                    if (entries.length === 0) entries = parseLogFile(content, file.name);

                    if (entries.length > 0) {
                        entries.forEach(e => {
                            e.source = `gcs:${cleanBucketName}`;

                            // Determine display type
                            let type = 'storage';

                            if (e.source_info) {
                                e.source_info.origin = `gcs:${cleanBucketName}`;
                                e.source_info.type = type;
                            } else {
                                e.source_info = {
                                    type: type,
                                    origin: `gcs:${cleanBucketName}`,
                                    file_identifier: file.name,
                                    raw_url: file.mediaLink
                                };
                            }
                            e.raw_url = `https://storage.googleapis.com/${cleanBucketName}/${file.name}`;
                            // Normalization Heuristics
                            if (e.latency?.mean && e.latency.mean < 100) {
                                e.latency.mean *= 1000;
                                if (e.latency.p50) e.latency.p50 *= 1000;
                                if (e.latency.p99) e.latency.p99 *= 1000;
                            }
                            if (e.ttft?.mean && e.ttft.mean < 100) {
                                e.ttft.mean *= 1000;
                                if (e.ttft.p50) e.ttft.p50 *= 1000;
                            }
                            newEntries.push(e);
                        });
                        newFileMetadata.push({ name: file.name, entryCount: entries.length });
                    }
                } catch (e) {
                    console.warn(`Failed to process new file ${file.name}:`, e);
                }
            }));

            return { newEntries, newFiles: newFileMetadata };

        } catch (e) {
            console.error("Delta fetch failed", e);
            throw e;
        }
    };

    const refreshSource = async (sourceType, id, mode = 'full') => {
        setGcsLoading(true);
        try {
            if (sourceType === 'gcs') {
                if (mode === 'delta') {
                    const currentProfile = gcsProfiles.find(p => p.bucketName === id);
                    if (!currentProfile) throw new Error("Profile not found for comparison");

                    const { newEntries, newFiles } = await fetchBucketDelta(id, currentProfile);

                    if (newEntries.length > 0) {
                        updateSourceData(`gcs:${id}`, newEntries, {
                            ...currentProfile,
                            files: [...(currentProfile.files || []), ...newFiles],
                            entryCount: (currentProfile.entryCount || 0) + newEntries.length,
                            loadedAt: new Date().toISOString()
                        }, 'append');
                        setGcsSuccess(`Added ${newEntries.length} new benchmarks.`);
                    } else {
                        setGcsSuccess(`No new data found in ${id}.`);
                    }
                } else {
                    // Full Refresh
                    setGcsProgress({});
                    const prefix = getPrefixForBucket(id);
                    const result = await fetchBucketData(id, true, prefix, handleProgress);
                    if (result.profile.error) {
                        setGcsError(result.profile.error);
                    } else {
                        updateSourceData(`gcs:${id}`, result.entries, result.profile, 'replace');
                        setGcsSuccess(`Refreshed bucket: ${id}`);
                    }
                }
            } else if (sourceType === 'giq') {
                let token = apiConfigs.find(c => c.projectId === id)?.token;
                if (!token) token = localStorage.getItem(`giq_token_${id}`);

                await CacheManager.remove('giq', id);

                let result = await fetchGiqData(id, token, false);

                if (result.profile.error && token && (result.profile.error.includes('401') || result.profile.error.includes('403'))) {
                    console.log(`[Refresh] Token expired for ${id}. Retrying with ADC...`);
                    const retryRes = await fetchGiqData(id, '', true);
                    if (!retryRes.profile.error) {
                        result = retryRes;
                    }
                }

                if (result.profile.error) {
                    setApiError(result.profile.error);
                } else {
                    updateSourceData(`giq:${id}`, result.entries, { ...result.profile, rawResponse: result.rawResponse }, mode === 'delta' ? 'merge' : 'replace');
                    setGcsSuccess(`Refreshed GIQ: Found ${result.rawResponse?.list?.profile?.length || '?'} profiles, Loaded ${result.entries.length} benchmarks.`);
                }
            }
            setTimeout(() => setGcsSuccess(null), 3000);
        } catch (e) {
            console.error("Refresh failed", e);
            setGcsError(`Refresh failed: ${e.message}`);
        }
        setGcsLoading(false);
    };

    const [submissions, setSubmissions] = useState([]);
    const [isLoadingSubmissions, setIsLoadingSubmissions] = useState(false);

    const mapStateToStatus = useCallback((state) => {
        return state;
    }, []);

    const loadSubmissions = useCallback(async (isManual = false) => {
        setIsLoadingSubmissions(true);
        try {
            const headers = {};
            if (accessToken) {
                headers['X-Prism-Github-Token'] = accessToken;
            }

            // Fetch user's own submissions as well as global unlisted submissions
            const [resOwn, resUnlisted] = await Promise.all([
                fetch('/api/results?own=true&limit=50', { headers }).catch(() => null),
                fetch('/api/results?status=unlisted&limit=50', { headers }).catch(() => null)
            ]);

            const ownData = resOwn && resOwn.ok ? await resOwn.json() : { items: [] };
            const unlistedData = resUnlisted && resUnlisted.ok ? await resUnlisted.json() : { items: [] };

            const itemsMap = new Map();
            (ownData.items || []).forEach(item => itemsMap.set(item.runId, item));
            (unlistedData.items || []).forEach(item => itemsMap.set(item.runId, item));
            const listDataItems = Array.from(itemsMap.values());
            
            const serverSubmissions = listDataItems.map(item => ({
                id: item.runId,
                runId: item.runId,
                model: item.model_name || "Custom Model",
                hardware: item.hardware?.hardware_name || "Detected Hardware",
                hardware_details: item.hardware || null,
                accelerator_count: item.hardware?.accelerator_count || 1,
                wellLitPath: item.well_lit_path || "none / custom",
                submittedAt: item.submitted_at ? item.submitted_at.split('T')[0] : "Unknown",
                status: mapStateToStatus(item.state),
                feedback: item.feedback || "",
                forked_from: item.forked_from || null,
                github_author: item.github_author || null
            }));

            const mergedList = [...serverSubmissions];
            if (brv02Runs && brv02Runs.length > 0) {
                brv02Runs.forEach(run => {
                    if (!mergedList.some(s => s.runId === run.runId)) {
                        const firstStage = run.stages?.[0];
                        const resolvedModel = run.model_name || firstStage?.scenario?.model || "Custom Model";
                        const resolvedHw = run.hardware?.hardware_name || firstStage?.scenario?.hardware || "Detected Hardware";
                        const resolvedAccCount = run.hardware?.accelerator_count || firstStage?.accelerator_count || 1;
                        const submittedAt = firstStage?.timestamp || new Date().toISOString();

                        mergedList.push({
                            id: `dyn-${run.runId}`,
                            runId: run.runId,
                            model: resolvedModel,
                            hardware: resolvedHw,
                            hardware_details: run.hardware || null,
                            accelerator_count: resolvedAccCount,
                            wellLitPath: run.wellLitPath || "none / custom",
                            submittedAt: typeof submittedAt === 'string' ? submittedAt.split('T')[0] : new Date().toISOString().split('T')[0],
                            status: "staged",
                            feedback: "",
                            forked_from: run.forked_from || run.payload?.forked_from || null,
                            github_author: run.github_author || run.payload?.github_author || null
                        });
                    }
                });
            }

            // Sort chronologically (latest submissions first)
            mergedList.sort((a, b) => b.submittedAt.localeCompare(a.submittedAt));
            setSubmissions(mergedList);

        } catch (error) {
            console.error("Failed to load submissions", error);
            if (addToast) {
                addToast(`Failed to load submissions: ${error.message}`, 'error');
            }
        } finally {
            setIsLoadingSubmissions(false);
        }
    }, [accessToken, brv02Runs, addToast, mapStateToStatus]);

    const updateSubmissionStatus = useCallback(async (runId, status, feedback = '') => {
        setIsLoadingSubmissions(true);
        try {
            const reviewer = user?.username || 'user'; // simple local fallback
            const headers = {
                'Content-Type': 'application/json',
            };
            if (accessToken) {
                headers['X-Prism-Github-Token'] = accessToken;
            }

            const isPromote = status === 'submitted_pending_review' || status === 'submitted_pending_processing';
            const endpoint = isPromote ? `/api/results/${runId}/promote` : `/api/results/${runId}/review`;
            const reqBody = isPromote
                ? { status }
                : { status, feedback, reviewer };

            const res = await fetch(endpoint, {
                method: 'POST',
                headers,
                body: JSON.stringify(reqBody)
            });
            if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
            const data = await res.json();
            if (data.success) {
                if (addToast) {
                    const friendlyStatus = 
                        status === 'submitted_pending_processing' ? 'submitted' :
                        status === 'submitted_pending_review' ? 'submitted for review' :
                        status === 'public' ? 'published' : status;
                    addToast(`Run has been ${friendlyStatus} successfully.`, 'success');
                }
                await CacheManager.clearAll();
                await loadSubmissions();
                if (loadAllData) {
                    await loadAllData(null, true);
                }
            }
        } catch (err) {
            console.error('[Status Update Error]', err);
            if (addToast) {
                addToast(`Failed to update status for run ${runId}: ${err.message}`, 'error');
            }
        } finally {
            setIsLoadingSubmissions(false);
        }
    }, [loadSubmissions, loadAllData, addToast, user, accessToken]);

    const bulkUpdateSubmissionStatus = useCallback(async (runIds, status, feedback = '') => {
        if (!runIds || runIds.length === 0) return;
        setIsLoadingSubmissions(true);
        try {
            const reviewer = user?.username || 'user';
            const headers = {
                'Content-Type': 'application/json',
            };
            if (accessToken) {
                headers['X-Prism-Github-Token'] = accessToken;
            }

            const isPromote = status === 'submitted_pending_review' || status === 'submitted_pending_processing';

            // Perform all updates concurrently
            const promises = runIds.map(async (runId) => {
                const endpoint = isPromote ? `/api/results/${runId}/promote` : `/api/results/${runId}/review`;
                const reqBody = isPromote
                    ? { status }
                    : { status, feedback, reviewer };

                const res = await fetch(endpoint, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify(reqBody)
                });
                if (!res.ok) {
                    throw new Error(`Failed to update status for run ${runId}: HTTP ${res.status}`);
                }
                return res.json();
            });

            await Promise.all(promises);

            if (addToast) {
                const friendlyStatus = 
                    status === 'submitted_pending_processing' ? 'submitted' :
                    status === 'submitted_pending_review' ? 'submitted for review' :
                    status === 'public' ? 'published' : status;
                addToast(`Successfully updated ${runIds.length} runs to ${friendlyStatus}.`, 'success');
            }
            
            await CacheManager.clearAll();
            await loadSubmissions();
            if (loadAllData) {
                await loadAllData(null, true);
            }
        } catch (err) {
            console.error('[Bulk Status Update Error]', err);
            if (addToast) {
                addToast(`Failed bulk update: ${err.message}`, 'error');
            }
        } finally {
            setIsLoadingSubmissions(false);
        }
    }, [loadSubmissions, loadAllData, addToast, user, accessToken]);

    const deleteSubmission = useCallback(async (runId, shouldReload = true) => {
        setIsLoadingSubmissions(true);
        try {
            const headers = {};
            if (accessToken) {
                headers['X-Prism-Github-Token'] = accessToken;
            }

            const res = await fetch(`/api/results/${runId}`, {
                method: 'DELETE',
                headers
            });
            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.error || `HTTP error! status: ${res.status}`);
            }
            const data = await res.json();
            if (data.success) {
                if (addToast) {
                    addToast(data.message || `Rejected benchmark ${runId} deleted permanently.`, 'success');
                }
                if (shouldReload) {
                    // Clear GCS IndexedDB cache and force-refetch data from GCS Results Store
                    await CacheManager.clearAll();
                    await loadSubmissions();
                    if (loadAllData) {
                        await loadAllData(null, true);
                    }
                }
            }
        } catch (err) {
            console.error('[Delete Submission Error]', err);
            if (addToast) {
                addToast(`Failed to delete benchmark run ${runId}: ${err.message}`, 'error');
            }
        } finally {
            setIsLoadingSubmissions(false);
        }
    }, [loadSubmissions, loadAllData, addToast, accessToken]);

    const submissionsMap = useMemo(() => {
        const map = {};
        (submissions || []).forEach(sub => {
            if (sub && sub.runId) {
                map[sub.runId] = sub;
            }
        });
        return map;
    }, [submissions]);

    const injectDynamicEntries = useCallback((newEntries) => {
        if (!newEntries || newEntries.length === 0) return;
        setData(prev => {
            const existingKeys = new Set(prev.map(d => `${d.run_id || d.source}:${d.workload?.input_tokens || d.isl || 0}x${d.workload?.output_tokens || d.osl || 0}`));
            const filteredNew = newEntries.filter(e => {
                const k = `${e.run_id || e.source}:${e.workload?.input_tokens || e.isl || 0}x${e.workload?.output_tokens || e.osl || 0}`;
                return !existingKeys.has(k);
            });
            return [...prev, ...filteredNew];
        });
    }, []);

    useEffect(() => {
        loadSubmissions();
    }, [loadSubmissions]);

    return {
        data, setData, injectDynamicEntries,
        loading, setLoading,
        isRestoringConnections,
        gcsProgressStats,
        loadingTasks,
        gcsLoading, setGcsLoading,
        gcsError, setGcsError,
        gcsSuccess, setGcsSuccess,
        apiError, setApiError,
        refreshSource,
        lpgLoading, setLpgLoading,
        lpgError, setLpgError,
        lpgPasteText, setLpgPasteText,
        driveLoading, setDriveLoading,
        driveStatus, setDriveStatus,
        driveProgress, setDriveProgress,
        driveError, setDriveError,
        qualityMetrics, setQualityMetrics,
        availableSources, setAvailableSources,
        selectedSources, setSelectedSources,
        bucketConfigs, setBucketConfigs,
        apiConfigs, setApiConfigs,
        gcsProfiles, setGcsProfiles,
        enableLLMDResults, setEnableLLMDResults,
        toasts, setToasts,
        addToast, removeToast,
        siteName, setSiteName,
        contactUrl, setContactUrl,
        gitCommit, setGitCommit,
        gitDescribe, setGitDescribe,
        fetchConfig, fetchBucketData, fetchGiqData,
        fetchQualityData, fetchLocalData, fetchArchivedData,
        loadAllData, handleLpgFileUpload, handleLpgGcsScan, handleLpgGcsLoad, syncDriveData,
        restoreSampleData, removeSampleData, removeLLMDData,
        newBucketName, setNewBucketName,
        newBucketAlias, setNewBucketAlias,
        connectionType, setConnectionType,
        newProjectId, setNewProjectId,
        newAuthToken, setNewAuthToken,
        showSampleData, setShowSampleData,
        expandedModels, setExpandedModels,
        debugInfo, setDebugInfo,
        API_KEY,
        qualityInspectOpen, setQualityInspectOpen,
        expandedIntegration, setExpandedIntegration,
        awsBucketConfigs, setAwsBucketConfigs,
        fetchAWSBucketData, handleAddAWSBucket, removeAWSBucket,
        handleAddGCSBucket, removeGCSBucket,
        brv02Runs, brv02Error, setBrv02Error, brv02Loading, handleBrv02Upload, handleValidatedUpload, removeBrv02Run, promoteStagedRunId, clearAllBrv02Runs, scannedFilenames,
        brv02CustomLabels, setBrv02CustomLabels,
        brv02BaselineRunId, setBrv02BaselineRunId,
        brv02SelectedStages, setBrv02SelectedStages,
        submissions, isLoadingSubmissions, loadSubmissions, updateSubmissionStatus, bulkUpdateSubmissionStatus, deleteSubmission, submissionsMap
    };
};